import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { TextField } from '@mui/material'
import { useUpdateProject } from '@/service-library/hooks/projects'
import { Project } from '@/types/projects'

type ProjectDescriptionFieldProps = {
  /** Put on the textarea, so a `SettingsRow` label can point at it. */
  id: string
  project: Project
  updateProject: ReturnType<typeof useUpdateProject>['updateProject']
}

/**
 * The project description, edited in place. It saves when the field loses
 * focus. Escape puts back the saved description.
 */
export default function ProjectDescriptionField({
  id,
  project,
  updateProject,
}: ProjectDescriptionFieldProps) {
  const savedDescription = project.description || ''
  const [description, setDescription] = useState(savedDescription)
  // Escape blurs the field before the reverted value renders, so the blur
  // handler would otherwise save the text that was just thrown away.
  const isCancellingRef = useRef(false)

  // Follow the saved description, including the rollback when a save fails.
  useEffect(() => {
    setDescription(savedDescription)
  }, [savedDescription])

  function handleBlur() {
    if (isCancellingRef.current) {
      isCancellingRef.current = false
      return
    }
    const trimmedDescription = description.trim()
    if (trimmedDescription === savedDescription) return
    updateProject({ ...project, description: trimmedDescription })
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return
    isCancellingRef.current = true
    setDescription(savedDescription)
    ;(e.target as HTMLElement).blur()
  }

  return (
    <TextField
      id={id}
      variant="outlined"
      multiline
      minRows={3}
      fullWidth
      placeholder="Describe the type of document that goes into this project"
      value={description}
      onChange={(e) => setDescription(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  )
}
